import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, MinusCircle, ExternalLink, Gauge } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import ShareToXButton from '@/components/ShareToXButton';
import { megaTierShareText, MEGA_TIER_HASHTAGS } from './megaTierShareText';

const WHAT = {
  pe: 'Price divided by diluted earnings per share from the latest annual filing. Graham refused to pay more than 15 years of current earnings.',
  pb: 'Price divided by book value per share. Graham allowed 1.5×, or a P/E × P/B product of 22.5 — the "Graham Number" rule that lets a cheap-on-earnings stock carry a slightly richer book multiple.',
  current_ratio: 'Current assets ÷ current liabilities. Can the company pay next year\'s bills from what it already has? Graham asked for 2×; DiviDrip uses 1.5×.',
  debt_vs_wc: 'Long-term debt compared with working capital (current assets − current liabilities). If liquid assets alone could retire the debt, leverage is not a threat.',
  earnings: 'Diluted EPS must be positive in both of the last two fiscal years — a one-year profit can be an accounting fluke.',
  roa_positive: 'Net income ÷ total assets. The business earned money on what it owns this year.',
  cfo_positive: 'Net cash from operating activities. Cash actually came in the door, regardless of accounting profit.',
  roa_improving: 'This year\'s return on assets versus last year\'s. Earning power is improving, not fading.',
  accruals: 'Operating cash flow greater than net income means profits are backed by cash rather than accruals — the earnings-quality test.',
  leverage_down: 'Long-term debt ÷ total assets fell year over year. The company is deleveraging rather than borrowing to survive.',
  liquidity_up: 'Current ratio rose year over year — short-term liquidity improved.',
  no_dilution: 'Shares outstanding did not increase. The company is not funding itself by diluting you.',
  margin_up: 'Gross margin (gross profit ÷ revenue) rose — pricing power or cost control.',
  turnover_up: 'Revenue ÷ total assets rose — more sales per dollar of assets.',
};

function CheckRow({ c }) {
  const state = c.pass === null || c.pass === undefined ? 'na' : c.pass ? 'pass' : 'fail';
  const Icon = state === 'pass' ? CheckCircle2 : state === 'fail' ? XCircle : MinusCircle;
  const tone = state === 'pass' ? 'text-emerald-500' : state === 'fail' ? 'text-rose-500' : 'text-muted-foreground';
  return (
    <div className="flex items-start gap-3 py-2 border-b border-border/40 last:border-0" data-testid={`mega-tier-check-${c.key}`}>
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${tone}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs font-semibold">{c.label}</span>
          <span className={`text-[10px] font-black uppercase ${tone}`}>{state === 'na' ? 'no data' : state}</span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{WHAT[c.key]}</p>
        <p className="text-[11px] mt-1 leading-snug">
          <span className="text-muted-foreground">Needs:</span> {c.target || '—'}
          <span className="mx-1.5 text-muted-foreground">·</span>
          <span className="text-muted-foreground">Where it is:</span> <span className="font-mono">{c.value || 'not stored yet — re-run the funnel'}</span>
        </p>
      </div>
    </div>
  );
}

export function MegaTierChecksModal({ row, kind, onClose, onOpenStockModal }) {
  const isGraham = kind === 'graham';
  const items = row ? (isGraham ? row.graham_checks : row.f_signals) || [] : [];
  const score = row ? (isGraham ? `${row.graham_score}/5` : `${row.f_score}/9`) : '';
  const passed = row ? (isGraham ? row.graham_score >= 4 : row.f_score >= 7) : false;
  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      {row && (
        <DialogContent className="max-w-sm p-0 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="mega-tier-checks-modal">
          <div className={`px-4 py-3 ${passed ? 'bg-emerald-600' : 'bg-rose-600'}`}>
            <DialogTitle className="text-white text-lg font-black flex items-center justify-between mr-14">
              <span data-testid="mega-tier-checks-ticker">{row.ticker} <span className="text-sm font-bold opacity-90">${Number(row.price).toFixed(2)}</span></span>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-black/25 whitespace-nowrap" data-testid="mega-tier-checks-score">
                {isGraham ? 'GRAHAM' : 'F-SCORE'} {score}
              </span>
            </DialogTitle>
            <p className="text-white/85 text-xs mt-0.5 truncate">{row.name}{row.sector ? ` · ${row.sector}` : ''}</p>
            <p className="text-white/85 text-[11px] mt-0.5">
              {isGraham ? 'Stage 2 gate: at least 4 of 5 must pass.' : 'Stage 3 gate: at least 7 of 9 must pass. Compared with the prior fiscal year.'}
              {row.fiscal_year_end ? ` FY ending ${row.fiscal_year_end}.` : ''}
            </p>
          </div>
          <div className="px-4 py-2">
            {items.map((c) => <CheckRow key={c.key} c={c} />)}
            <Link to={`/stock/${row.ticker}`} className="flex items-center justify-center gap-1.5 w-full py-2 mt-3 mb-1.5 rounded-lg border text-xs font-bold hover:bg-muted/50 transition-colors" data-testid="mega-tier-checks-stock-page-link">
              OPEN {row.ticker} IN STOCK PAGE <ExternalLink className="w-3 h-3" />
            </Link>
            <button type="button" onClick={() => { onClose(); onOpenStockModal?.(row.ticker); }}
              className="flex items-center justify-center gap-1.5 w-full py-2 mb-1.5 rounded-lg border text-xs font-bold hover:bg-muted/50 transition-colors"
              data-testid="mega-tier-checks-open-stock-modal-btn">
              OPEN {row.ticker} IN STOCK MODAL <Gauge className="w-3 h-3" />
            </button>
            <button type="button" onClick={onClose}
              className="w-full py-2 mb-2 rounded-lg bg-muted text-xs font-bold hover:bg-muted/70 transition-colors"
              data-testid="mega-tier-checks-close-btn">
              CLOSE
            </button>
            <div className="flex justify-center pb-2" data-testid="mega-tier-checks-share">
              <ShareToXButton variant="button" ticker={row.ticker} text={megaTierShareText(row)} hashtags={MEGA_TIER_HASHTAGS} label={`Share $${row.ticker} on X`} />
            </div>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}
